import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getClassificationDefaultTitle, getClassificationValues } from "../../../../ducks/classification/selectors";
import { useClassificationUpdate } from "../../../Classification/hooks/useClassificationUpdate";
import { useDialogReset } from "../../../../hooks/useDialogs";
import { useClassificationSegment } from "./useClassificationSegment";
import { isRangeClasses, isUniqueClasses } from "../../../../interfaces/classification";

type ValueTitle = {
  title: string;
  saveTitle: VoidFunction;
  updateTitle: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const useClassificationTitle = (index: number): ValueTitle => {
  const { t } = useTranslation("common");
  const { onChange, setDefaultTitle } = useClassificationUpdate();
  const values = useSelector(getClassificationValues);
  const classificationSegment = useClassificationSegment();
  const defaultTitle = useSelector(getClassificationDefaultTitle);
  const resetDialog = useDialogReset();

  const titleFromStore = useMemo(() => {
    const defaultValue = classificationSegment?.defaultTitle || defaultTitle;
    const currentValues = classificationSegment?.values || values;

    if (!currentValues) return defaultValue;

    if (isRangeClasses(currentValues)) {
      return currentValues[index]?.from
        ? `${currentValues[index].from} - ${currentValues[index].to}`
        : t("classification.other");
    }
    if (isUniqueClasses(currentValues)) {
      return currentValues[index]?.uniqueValue ? currentValues[index].uniqueValue : t("classification.other");
    }

    return currentValues[index]?.title || defaultValue;
  }, [classificationSegment?.defaultTitle, classificationSegment?.values, defaultTitle, values, index, t]);

  const [title, setTitle] = useState(titleFromStore);

  const saveTitle = useCallback(() => {
    if (+index >= 0) {
      onChange({ value: title, index: +index, field: "title" });
    } else {
      setDefaultTitle(title);
    }

    resetDialog();
  }, [index, resetDialog, onChange, title, setDefaultTitle]);

  const updateTitle = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  }, []);

  return {
    title,
    saveTitle,
    updateTitle,
  };
};
